/* ═══════════════════════════════════════════════════════════════
   ProjectsPage — Project Center R1 shell. Owns firm-wide data (the
   old combined Projects page's `loadData`), the live-sync listener, which
   project (if any) is open, and deep-link landing. Renders the Hub
   list or a single project's Dashboard.
   ═══════════════════════════════════════════════════════════════ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { list as cacheList, listCached } from '../../services/cloudCache';
import { ProjectService } from '../../services/projectService';
import { OrgService } from '../../services/orgService';
import { AdminService } from '../../services/adminService';
import { TeamService } from '../../services/teamService';
import { DepartmentService } from '../../services/departmentService';
import { ProjectSpecService } from '../../services/projectSpecService';
import type {
  Project,
  Organization,
  ProjectType,
  BillingType,
  ProjectPhase,
  Deliverable,
  ProjectScope,
  ProjectScopeAssignment,
  TeamMember,
  ProjectAssignment,
  Team,
  TeamAssignment,
  ProjectSpec,
} from '../../../shared/types';
import type { Department } from '../../../shared/entities';
import { hasPermission } from '../../../shared/permissions';
import type { AuthUser } from '../../services/authService';
import type { DeepLink } from '../../types/navigation';
import { Hub } from './Hub';
import { Dashboard } from './Dashboard';
import { ProjectFormPanel } from './ProjectFormPanel';
import { computeAttention } from './projectMutations';
import { toAddress } from '../../routing/address';
import { useAddressSync } from '../../routing/useAddressSync';
import type { DashboardFocus, FirmPhaseDef } from './widgets/context';

export interface ProjectsProps {
  toast: (msg: string, isError?: boolean) => void;
  onOpenProjectSpec?: (specId: string, projectId: string, opts?: { review?: boolean }) => void;
  initialProjectId?: string;
  authUser?: AuthUser | null;
  /** Cross-page navigation function. Dashboard widgets (T4) call this via
   *  the drill router to land on TimeExpense / Invoices / Team etc. */
  navigate?: (link: DeepLink) => void;
  /** One-shot deep link landing on this page (e.g. open project X). */
  deepLink?: {
    view: 'projects';
    projectId?: string;
    tab?: 'dashboard' | 'specs' | 'formas';
  } | null;
  /** Open a forma in the designer (host swaps to the editor view). */
  onOpenForma?: (formaId: string, opts?: { review?: boolean }) => void;
  onDeepLinkConsumed?: () => void;
}

function blankProject(): Project {
  return {
    id: '',
    name: '',
    projectNumber: '',
    status: 'active',
    selections: {},
    createdAt: '',
    updatedAt: '',
  };
}

export default function ProjectsPage({
  toast,
  authUser,
  onOpenProjectSpec,
  onOpenForma,
  initialProjectId,
  navigate,
  deepLink,
  onDeepLinkConsumed,
}: ProjectsProps) {
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [orgs, setOrgs] = useState<Organization[]>(() => listCached<Organization>('organizations'));
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>(() =>
    listCached<ProjectType>('project_types')
  );
  const [billingTypes, setBillingTypes] = useState<BillingType[]>(() =>
    listCached<BillingType>('billing_types')
  );
  const [firmPhases, setFirmPhases] = useState<FirmPhaseDef[]>([]);
  const [allPhases, setAllPhases] = useState<ProjectPhase[]>([]);
  const [allDeliverables, setAllDeliverables] = useState<Deliverable[]>([]);
  const [allScopes, setAllScopes] = useState<ProjectScope[]>([]);
  const [allScopeAssignments, setAllScopeAssignments] = useState<ProjectScopeAssignment[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allProjectSpecs, setAllProjectSpecs] = useState<ProjectSpec[]>(() =>
    listCached<ProjectSpec>('project_specs')
  );
  const [allProjectAssignments, setAllProjectAssignments] = useState<ProjectAssignment[]>(() =>
    listCached<ProjectAssignment>('project_assignments')
  );
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [focus, setFocus] = useState<DashboardFocus | null>(null);
  // Stays mounted always (never `{open && <Panel/>}`) so Radix's Sheet can
  // run its own close transition and restore focus to the trigger (New
  // project button) — an unmount-on-close would drop focus to <body>.
  // `formPanel` holds the last config even while closed; ProjectFormPanel
  // resets its own draft from `project` whenever `open` flips true.
  const [formPanelOpen, setFormPanelOpen] = useState(false);
  const [formPanel, setFormPanel] = useState<{ mode: 'create' | 'edit'; project: Project }>({
    mode: 'create',
    project: blankProject(),
  });

  const settle = useCallback(function settle<T>(r: PromiseSettledResult<T>, fallback: T): T {
    if (r.status === 'fulfilled') return r.value;
    console.error('[projects] loadData source failed:', r.reason);
    return fallback;
  }, []);

  // Promise.allSettled — one failing source degrades to empty instead of
  // blanking the whole hub (design law: data never blank, always live).
  // Stable via useCallback (empty deps: every dependency is a setState
  // setter) so the effects below can name it without a refetch loop.
  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        ProjectService.list(),
        OrgService.list(),
        AdminService.projectTypes.list(),
        AdminService.billingTypes.list(),
        AdminService.defaultPhases.list(),
        AdminService.projectScopes.list(),
        AdminService.projectScopeAssignments.list(),
        cacheList<ProjectPhase>('project_phases'),
        cacheList<Deliverable>('deliverables'),
        TeamService.list(),
        TeamService.listTeams(),
        TeamService.listTeamAssignments(),
        DepartmentService.list(),
        ProjectSpecService.list(),
        cacheList<ProjectAssignment>('project_assignments'),
      ] as const);
      setProjects(settle(results[0], [] as Project[]));
      setOrgs(settle(results[1], [] as Organization[]));
      setProjectTypes(settle(results[2], [] as ProjectType[]));
      setBillingTypes(settle(results[3], [] as BillingType[]));
      setFirmPhases(settle(results[4], [] as FirmPhaseDef[]));
      setAllScopes(settle(results[5], [] as ProjectScope[]));
      setAllScopeAssignments(settle(results[6], [] as ProjectScopeAssignment[]));
      setAllPhases(settle(results[7], [] as ProjectPhase[]));
      setAllDeliverables(settle(results[8], [] as Deliverable[]));
      setMembers(settle(results[9], [] as TeamMember[]));
      setTeams(settle(results[10], [] as Team[]));
      setTeamAssignments(settle(results[11], [] as TeamAssignment[]));
      setDepartments(settle(results[12], [] as Department[]));
      setAllProjectSpecs(settle(results[13], [] as ProjectSpec[]));
      setAllProjectAssignments(settle(results[14], [] as ProjectAssignment[]));
      setLoadStatus('ready');
    } catch (e) {
      console.error('[projects] loadData failed:', e);
      setLoadStatus('error');
    }
  }, [settle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [loadData]);

  // Auto-open a project passed in on mount (e.g. returning from a spec).
  useEffect(() => {
    if (initialProjectId && projects.length > 0 && !openProjectId) {
      const proj = projects.find((p) => p.id === initialProjectId);
      if (proj) setOpenProjectId(proj.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProjectId, projects.length]);

  // One-shot deep link: open a project, optionally focused on a tab/widget.
  useEffect(() => {
    if (!deepLink || deepLink.view !== 'projects') return;
    if (deepLink.projectId) {
      setOpenProjectId(deepLink.projectId);
      if (deepLink.tab === 'specs') setFocus({ kind: 'takeover', widgetId: 'specs' });
      // Forma folded into Specs as a strip (Widgets R2 Decision 5) — the
      // 'formas' deep-link tab now focuses the Specs widget.
      else if (deepLink.tab === 'formas') setFocus({ kind: 'widget', widgetId: 'specs' });
      else setFocus(null);
    }
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const attentionItems = useMemo(
    () => computeAttention(projects, allDeliverables, allProjectAssignments, new Date()),
    [projects, allDeliverables, allProjectAssignments]
  );

  const openProject = openProjectId ? projects.find((p) => p.id === openProjectId) || null : null;

  // Address: the open project's address; Back from it returns to the list.
  useAddressSync({
    address: openProject ? toAddress({ view: 'projects', projectId: openProject.id }) : null,
    listPath: '/projects',
    onLeave: () => {
      setOpenProjectId(null);
      setFocus(null);
    },
    title: openProject ? openProject.projectNumber || openProject.name || null : null,
  });

  return (
    <>
      {openProject ? (
        <Dashboard
          project={openProject}
          focus={focus}
          onBack={() => {
            setOpenProjectId(null);
            setFocus(null);
          }}
          authUser={authUser}
          toast={toast}
          navigate={navigate}
          onOpenProjectSpec={onOpenProjectSpec}
          onOpenForma={onOpenForma}
          onProjectChange={(p) => setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))}
          orgs={orgs}
          projectTypes={projectTypes}
          billingTypes={billingTypes}
          firmPhases={firmPhases}
          allScopes={allScopes}
          members={members}
          teams={teams}
          teamAssignments={teamAssignments}
          departments={departments}
        />
      ) : (
        <Hub
          projects={projects}
          orgs={orgs}
          projectTypes={projectTypes}
          billingTypes={billingTypes}
          allPhases={allPhases}
          allDeliverables={allDeliverables}
          allScopes={allScopes}
          allScopeAssignments={allScopeAssignments}
          allProjectSpecs={allProjectSpecs}
          attentionItems={attentionItems}
          attentionStatus={loadStatus}
          authUser={authUser}
          toast={toast}
          onOpenProject={(id, f) => {
            setOpenProjectId(id);
            setFocus(f || null);
          }}
          onNewProject={() => {
            setFormPanel({ mode: 'create', project: blankProject() });
            setFormPanelOpen(true);
          }}
          reloadProjects={loadData}
        />
      )}

      <ProjectFormPanel
        open={formPanelOpen}
        mode={formPanel.mode}
        project={formPanel.project}
        orgs={orgs}
        projectTypes={projectTypes}
        billingTypes={billingTypes}
        members={members}
        firmPhases={firmPhases}
        allScopes={allScopes}
        scopeAssignments={allScopeAssignments.filter((a) => a.projectId === formPanel.project.id)}
        phases={allPhases.filter((p) => p.projectId === formPanel.project.id)}
        onSaved={(saved) => {
          void loadData();
          setOpenProjectId(saved.id);
        }}
        onClose={() => setFormPanelOpen(false)}
        toast={toast}
        canViewFinancials={hasPermission(authUser?.permissions, 'projects.view_financials')}
      />
    </>
  );
}
